// Adaptador de la antigua API REST (back PHP) a Supabase.
//
// Los componentes siguen llamando api.get('/transactions'), api.post('/goals', ...), etc.
// Cada ruta se resuelve con una función SQL (public.api_*) que devuelve el mismo JSON que
// devolvía el back, así que los componentes no cambian. Mantiene la forma de axios:
// las respuestas son { status, data } y los errores traen error.response.{status,data}.
import { supabaseClient } from "../services/supabase";

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.response = { status, data };
  }
}

// Códigos de PostgreSQL/PostgREST → HTTP, para que la lógica de reintentos de los
// componentes (409/422 = ya existe, 5xx = reintentar) siga funcionando.
const statusFromError = (error) => {
  switch (error?.code) {
    case '23505': return 409;             // unique_violation
    case 'P0001': return 422;             // raise exception (límites del plan)
    case 'P0002': return 404;             // no_data_found
    case '42501': return 403;             // insufficient_privilege / RLS
    case 'PGRST301':
    case 'PGRST303': return 401;          // JWT inválido o expirado
    default: return 500;
  }
};

const rpc = async (fn, args = {}) => {
  const { data, error } = await supabaseClient.rpc(fn, args);
  if (error) {
    throw new ApiError(error.message, statusFromError(error), { error: error.message, hint: error.hint });
  }
  return data;
};

const pagingArgs = (query) => {
  const limit = Math.min(Math.max(Number(query.get('limit')) || 100, 1), 5000);
  const page = Math.max(Number(query.get('page')) || 1, 1);
  const skip = Number(query.get('offset') ?? query.get('skip')) || (page - 1) * limit;
  return { p_limit: limit, p_skip: Math.max(skip, 0) };
};

// [método, patrón, (params del match, query, body) => Promise<data>]
// /expense/:id es el nombre que usa el front para /transaction/:id.
const routes = [
  ['get', /^\/dashboard$/, (m, q) => rpc('api_dashboard', pagingArgs(q))],
  ['get', /^\/dashboard\/categories$/, () => rpc('api_dashboard_categories')],
  ['get', /^\/dashboard\/monthly$/, () => rpc('api_dashboard_monthly')],
  ['get', /^\/dashboard\/tags$/, () => rpc('api_dashboard_tags')],
  // Por defecto sólo gastos (lo que suman las pantallas); ?include=all trae también
  // ingresos y pagos de tarjeta, que necesita la pantalla de Transferencias.
  ['get', /^\/transactions$/, (m, q) =>
    rpc('api_transactions', { ...pagingArgs(q), p_only_expenses: q.get('include') !== 'all' })],
  ['get', /^\/(?:transaction|expense)\/(\d+)$/, (m) => rpc('api_transaction', { p_id: Number(m[1]) })],
  ['get', /^\/(?:transaction|expense)\/(\d+)\/tags$/, (m) => rpc('api_transaction_tags', { p_transaction_id: Number(m[1]) })],
  ['post', /^\/(?:transaction|expense)\/(\d+)\/tags\/add$/, (m, q, body) =>
    rpc('api_add_transaction_tag', { p_transaction_id: Number(m[1]), p_tag: String(body?.tag ?? body?.name ?? '') })],
  ['delete', /^\/(?:transaction|expense)\/(\d+)\/tags$/, (m, q, body) =>
    rpc('api_remove_transaction_tag', { p_transaction_id: Number(m[1]), p_tag: String(body?.tag ?? body?.name ?? '') })],
  ['get', /^\/categories$/, () => rpc('api_categories')],
  ['get', /^\/goals$/, () => rpc('api_goals')],
  ['get', /^\/goals\/categories$/, () => rpc('api_goal_categories')],
  ['post', /^\/goals$/, async (m, q, body) => {
    const result = await rpc('api_set_goal', {
      p_goal: Math.round(Number(body?.goal) || 0),
      p_category_id: body?.category_id ?? null,
      p_category: body?.category ?? null,
    });
    if (!result?.success) throw new ApiError(result?.error ?? 'No se pudo guardar la meta', 422, result);
    return result;
  }],
  ['get', /^\/tags$/, () => rpc('api_tags')],
  ['post', /^\/tags\/add$/, (m, q, body) => rpc('api_add_tag', { p_name: String(body?.name ?? body?.tag ?? '') })],
  ['get', /^\/uploads$/, (m, q) => rpc('api_uploads', { p_limit: Number(q.get('limit')) || 5 })],
  ['get', /^\/data\/banks$/, () => rpc('api_banks')],
  ['get', /^\/data\/card_types$/, () => rpc('api_card_types')],
  ['get', /^\/(?:auth\/)?user$/, () => rpc('api_me')],
  // El back nunca tuvo /subscriptions: la pantalla detecta suscripciones en el cliente.
  ['get', /^\/subscriptions$/, async () => []],
  ['get', /^\/forecast$/, async () => ({ message: 'Forecast' })],
];

const request = async (method, url, body) => {
  const parsed = new URL(url, 'http://local');
  const path = parsed.pathname.replace(/\/+$/, '') || '/';
  for (const [routeMethod, pattern, handler] of routes) {
    if (routeMethod !== method) continue;
    const match = path.match(pattern);
    if (match) {
      const data = await handler(match, parsed.searchParams, body);
      return { status: 200, data };
    }
  }
  throw new ApiError(`Ruta no soportada: ${method.toUpperCase()} ${path}`, 404, null);
};

const api = {
  get: (url) => request('get', url),
  post: (url, body) => request('post', url, body),
  delete: (url, config) => request('delete', url, config?.data),
};

// Subida de cartolas: Storage + fila en uploads + Edge Function que la procesa.
// Reemplaza a POST /upload (multipart) del back PHP.
const SUPPORTED_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'csv'];

const sha256 = async (file) => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const uploadStatement = async (file) => {
  const extension = (file.name.split('.').pop() || '').toLowerCase();
  if (!SUPPORTED_EXTENSIONS.includes(extension)) {
    throw new ApiError(`Formato no soportado: .${extension}`, 422, null);
  }
  const { data: { user } } = await supabaseClient.auth.getUser();
  if (!user) throw new ApiError('Sesión expirada', 401, null);

  const storagePath = `${user.id}/${crypto.randomUUID()}.${extension}`;
  const { error: storageError } = await supabaseClient.storage
    .from('statements')
    .upload(storagePath, file, { contentType: file.type || undefined, upsert: false });
  if (storageError) throw new ApiError(storageError.message, 500, null);

  const checksum = await sha256(file);
  const { data: upload, error: insertError } = await supabaseClient
    .from('uploads')
    .insert({ storage_path: storagePath, filename: file.name, file_type: extension, checksum })
    .select('id')
    .single();
  if (insertError) {
    await supabaseClient.storage.from('statements').remove([storagePath]);
    const status = statusFromError(insertError);
    if (status === 409) {
      // El mismo archivo ya se subió. Si esa vez falló el procesamiento, se reintenta en vez de
      // rechazarlo: de otro modo no habría forma de recuperar una cartola con error.
      const { data: previous } = await supabaseClient
        .from('uploads').select('id, status').eq('checksum', checksum).maybeSingle();
      if (previous?.status === 'error') {
        await processUpload(previous.id);
        return previous.id;
      }
    }
    const message = status === 409 ? 'Esta cartola ya estaba subida' : insertError.message;
    throw new ApiError(message, status, { error: message, hint: insertError.hint });
  }

  await processUpload(upload.id);
  return upload.id;
};

const processUpload = async (uploadId) => {
  const { error: invokeError } = await supabaseClient.functions.invoke('process-upload', {
    body: { upload_id: uploadId },
  });
  if (invokeError) throw new ApiError(invokeError.message, 500, { upload_id: uploadId });
};

export const getUploadStatus = async (uploadId) => {
  const { data, error } = await supabaseClient
    .from('uploads')
    .select('id, filename, status, error, rows_processed')
    .eq('id', uploadId)
    .single();
  if (error) throw new ApiError(error.message, statusFromError(error), null);
  return data;
};

// Se mantienen por compatibilidad con AuthContext; ya no hay requests HTTP que abortar.
export const abortControllers = {};
export const cancelAllRequests = () => {};

export default api;
